import { getMediaPlaylistMetadata, isResolvedTrack } from "./types/index.js";
import { getAllTracks } from "./utils/tracks.js";
import { SVTA_UNSUPPORTED_ENCRYPTION_METHOD } from "./errors.js";
import { toValue } from "@videojs/utils/function";
//#region src/media/drm.ts
/**
* DOM-free DRM model helpers: the `source.drm`-shaped config contract, HLS `KEYFORMAT` → EME key-system identity
* mapping, manifest-declared key collection, and key-system candidate selection. The browser-touching EME half (access
* negotiation, MediaKeys attachment, init-data decoding, license POST) lives in `dom/eme.ts`, which re-exports these.
*/
/**
* `state.negotiatedKeySystem` when negotiation ran and every candidate was refused.
*
* A sentinel rather than `null` or a second slot, so the slot stays `string | undefined` and its three meanings read
* off one value: absent means negotiation hasn't settled, this means it settled on nothing, anything else is the chosen
* key system. Cannot collide with a real id — those are reverse-DNS (`com.widevine.alpha`).
*
* The distinction is load-bearing for pruning: "not yet" must leave encrypted renditions alone, while "refused" is the
* late fact that makes them unplayable (see `excludeRefusedKeySystems`).
*/
const NO_KEY_SYSTEM = "none";
/**
* Resolve a {@link DrmUrl}. Called from a selection constraint, so a resolver that throws answers `undefined` rather
* than failing the pruning pass — see {@link toValue}.
*/
function resolveDrmUrl(url) {
	return toValue(url);
}
/** Resolve configured license-request headers. See {@link resolveDrmUrl}. */
function resolveDrmHeaders(headers) {
	return toValue(headers);
}
/** Resolve a configured fetch credentials mode. See {@link resolveDrmUrl}. */
function resolveDrmCredentials(credentials) {
	return toValue(credentials);
}
/**
* What a FairPlay content id is when a source names no {@link FairPlayContentId}: everything after the scheme, which is
* Apple's own sample convention and what Mux's license server expects.
*/
function defaultFairPlayContentId(keyUri) {
	const start = keyUri.indexOf("skd://");
	return start === -1 ? keyUri : keyUri.slice(start + 6);
}
/**
* Every DRM key declaration across the presentation's resolved tracks, deduped by full attribute identity. Empty until
* at least one encrypted rendition's media playlist has resolved — `EXT-X-KEY` is a media-playlist tag, and Mux emits
* no `EXT-X-SESSION-KEY` in the multivariant.
*/
function declaredDrmKeys(presentation) {
	const keys = [];
	const seen = /* @__PURE__ */ new Set();
	for (const track of getAllTracks(presentation?.selectionSets ?? [])) {
		if (!isResolvedTrack(track)) continue;
		for (const key of getMediaPlaylistMetadata(track)?.keys ?? []) {
			const identity = [
				key.method,
				key.uri,
				key.keyFormat,
				key.keyId,
				key.iv
			].join(" ");
			if (seen.has(identity)) continue;
			seen.add(identity);
			keys.push(key);
		}
	}
	return keys;
}
/** Encryption scheme per HLS `METHOD`, for the MKSA encryption-scheme query. */
const ENCRYPTION_SCHEME_BY_METHOD = {
	"SAMPLE-AES": "cbcs",
	"SAMPLE-AES-CTR": "cenc",
	"SAMPLE-AES-CENC": "cenc"
};
/**
* The one encryption scheme the declared keys use, or `undefined` when they mix schemes or declare none we recognize.
* Stamped onto negotiation capabilities so scheme-aware CDMs refuse content they can't decrypt (Mux serves `SAMPLE-AES`
* — cbcs); UAs predating the encryption-scheme query ignore the member, so declaring it never costs support.
*/
function declaredEncryptionScheme(keys) {
	const schemes = new Set(keys.map((key) => ENCRYPTION_SCHEME_BY_METHOD[key.method]).filter((scheme) => scheme !== void 0));
	return schemes.size === 1 ? [...schemes][0] : void 0;
}
/**
* The init data a presentation's manifest carries for one key system: every declared key the module claims, projected
* through its `toInitData` (Widevine PSSH / PlayReady PRO as `data:` URIs). Empty when the module carries none
* (FairPlay `skd://` keys have no inline init data) or declares no projection at all — which is what routes
* `exchangeLicenses` to its event-driven fallback.
*/
function manifestInitData(presentation, module_) {
	if (!module_?.toInitData) return [];
	const projected = [];
	for (const key of declaredDrmKeys(presentation)) {
		if (key.keyFormat === void 0 || key.uri === void 0 || !module_.keyFormats.includes(key.keyFormat)) continue;
		const initData = module_.toInitData(key.uri);
		if (initData) projected.push(initData);
	}
	return projected;
}
/**
* The key-system modules worth asking the CDM for: declared by the presentation's keys _and_ resolving to a license
* server, in `keySystems` order. Keys without a `KEYFORMAT` any module claims (e.g. `identity` AES-128) contribute
* nothing.
*
* Negotiation preference is the caller's array order rather than a table here — hls.js's order (the platform-native
* system first, FairPlay existing only on Apple UAs so it costs nothing elsewhere) is what `DEFAULT_KEY_SYSTEMS`
* encodes, and a composition that wants another just orders its own list.
*
* A resolved license server rather than a named entry is what counts, so a config naming every system it could ever
* license still refuses the sources it holds no credentials for.
*/
function keySystemCandidates(keys, drm, keySystems) {
	const declared = new Set(keys.map((key) => key.keyFormat).filter((keyFormat) => keyFormat !== void 0));
	return keySystems.filter((module_) => module_.keyFormats.some((keyFormat) => declared.has(keyFormat)) && resolveDrmUrl(drm[module_.keySystem]?.licenseUrl) !== void 0);
}
/**
* The HLS default `KEYFORMAT`: a key file (URL key), not a DRM key system. RFC 8216 makes it the value when the
* attribute is absent, so an absent or `identity` keyformat marks non-DRM (clear-key) encryption — `AES-128` /
* `SAMPLE-AES` decrypted from a fetched key, a different subsystem from EME (see `clear-key-aes.md`).
*/
const IDENTITY_KEY_FORMAT = "identity";
/**
* The first declared key that is non-DRM (`identity`-keyformat) encryption, or `undefined` when every declared key
* names a DRM key system. Distinguishes "the source is clear-key encrypted, which this engine can't decrypt" from "a
* DRM system was declared and refused or is unlicensable" when negotiation yields no usable key system.
*/
function firstNonDrmEncryptionKey(keys) {
	return keys.find((key) => key.keyFormat === void 0 || key.keyFormat === "identity");
}
/**
* The cause to report when encryption the engine cannot decrypt is why a source or rendition will not play, given its
* declared keys — or `undefined` when a DRM system is the right thing to blame. An `identity`-keyformat key (AES-128 /
* SAMPLE-AES over HTTP) is not EME at all and this engine has no decryptor for it, so the cause names the unsupported
* _encryption method_ rather than a DRM system that was never involved. `data` rides on the condition beside the method
* and key format.
*
* Shared by `setupMediaKeys` (at negotiation) and the condition reporter (at resolve), so the two never disagree about
* which gap to name.
*/
function unsupportedEncryptionMethodCause(keys, data = {}) {
	const nonDrmKey = firstNonDrmEncryptionKey(keys);
	if (!nonDrmKey) return void 0;
	return {
		code: SVTA_UNSUPPORTED_ENCRYPTION_METHOD,
		data: {
			...data,
			method: nonDrmKey.method,
			keyFormat: nonDrmKey.keyFormat
		}
	};
}
/**
* Build the engine-facing DRM config a Media hands its engine once: one entry per key system whose every field reads
* the _current_ source (via `getDrm`) at call time, so switching source re-licenses without rebuilding the engine.
*
* URL and header fields resolve to a plain value the engine re-resolves harmlessly. The four transform fields are
* stable functions that apply the current source's transform, or pass the value through when it names none. Transforms
* cannot ride {@link DrmUrl}'s resolver union: {@link toValue} tells a function from a value by `typeof ===
* 'function'`, which a transform — itself a function — collides with. Wrapping here is what lets a source carry
* transforms without the engine ever holding, or structurally comparing, the source's own function objects.
*/
function sourceDrmSystems(getDrm, keySystems) {
	return Object.fromEntries(keySystems.map(({ keySystem }) => {
		const entry = () => getDrm()?.[keySystem];
		return [keySystem, {
			licenseUrl: () => resolveDrmUrl(entry()?.licenseUrl),
			serverCertificateUrl: () => resolveDrmUrl(entry()?.serverCertificateUrl),
			headers: () => resolveDrmHeaders(entry()?.headers),
			certificateHeaders: () => resolveDrmHeaders(entry()?.certificateHeaders),
			credentials: () => resolveDrmCredentials(entry()?.credentials),
			licenseRequest: (request) => entry()?.licenseRequest?.(request) ?? request,
			licenseResponse: (response) => entry()?.licenseResponse?.(response) ?? response,
			certificateRequest: (request) => entry()?.certificateRequest?.(request) ?? request,
			certificateResponse: (response) => entry()?.certificateResponse?.(response) ?? response,
			fairPlayContentId: (keyUri) => entry()?.fairPlayContentId?.(keyUri) ?? defaultFairPlayContentId(keyUri)
		}];
	}));
}
//#endregion
export { IDENTITY_KEY_FORMAT, NO_KEY_SYSTEM, declaredDrmKeys, declaredEncryptionScheme, defaultFairPlayContentId, firstNonDrmEncryptionKey, keySystemCandidates, manifestInitData, resolveDrmCredentials, resolveDrmHeaders, resolveDrmUrl, sourceDrmSystems, unsupportedEncryptionMethodCause };

//# sourceMappingURL=drm.js.map